import { ComparisonTableModal } from '@/modules/AssetsStudioPage/SensorFusion/components/charts-section/comparison-table/ComparisonTableModal';
import { PlotlyChart } from '../visualization/';
import { SensorLegend } from './SensorLegend';
import { DataLayerLegend } from '../legend/DataLayerLegend';
import { ChartLoader } from './ChartLoader';
import type React from 'react';
import { useThresholds } from '@/hooks/shared/DataLayers';
import { ChartEmptyState, EMPTY_STATE_ENUM } from './ChartEmptyState';
import { useCallback, useEffect, useMemo, useRef, useState, Dispatch, SetStateAction } from 'react';
import clsx from 'clsx';
import { LegendSearchEntry } from '../../types';
import { generateTestId } from '@/utils/shared/generateTestIds';
import { SENSOR_FUSION_DATA_LAYERS } from '@/types/shared/navigation-tabs.enums';
import { MultiWaveSurferChart } from '@/components/pages/AssetsStudioPage/MainContent/SensorData/Waveform/components';
import type { WaveformTraceData, WaveformControls, WaveformMarker } from '@/components/pages/AssetsStudioPage/MainContent/SensorData/Waveform/components';

interface MetricConfig {
  key: string;
  label: string;
  unit: string;
  show: boolean;
  sensorColors: Record<string, string>;
  icon?: React.ReactNode;
}

interface DataLayerChartViewProps {
  isLoadingState: boolean;
  isFeatureLoading?: boolean;
  componentId?: string;
  isSplit: boolean;
  isDiscrete?: boolean;
  showComparisonTable: boolean;
  config: {
    sensorUiNames: string[];
  };
  metrics: MetricConfig[];
  data: any;
  customVerticalLines?: any;
  enabledSensors: Record<string, boolean>;
  verticalLines: any[];
  loadingLineValues?: Set<string>;
  intervalLines: any[];
  sharedTimeRange: [Date, Date] | null;
  selectionMode: boolean;
  selectedRanges: any;
  globalSelectionId?: string | null;
  handleToggleSensor: (sensor: string) => void;
  handleAddSensor: (sensor: string, withScroll?: boolean) => void;
  isSensorLoading: (sensorKey: string) => boolean;
  hasCachedDataForSensor?: (sensorName: string) => boolean;
  addVerticalLine: (
    timestamp: Date | number,
    values: Record<string, number>,
    xValue?: string | number,
    customLabel?: string,
    customColor?: string,
    isHarmonic?: boolean,
    isFirstHarmonic?: boolean,
    chartId?: string,
    sectionKey?: string,
    markerType?: 'system' | 'user',
    originalPrefix?: string,
    groupId?: string
  ) => void;
  removeVerticalLine: (id: string) => void;
  updateVerticalLine?: (lineId: string, updates: Partial<any>) => void;
  handleRangeChange: (range: [Date, Date] | null) => void;
  handleEditThresholds: (chartType: string, sensorName?: string) => void;
  removeIntervalLine: (id: string) => void;
  handleAddSelectedRange: (startTime: Date, endTime: Date, chartId: string) => void;
  handleRemoveSelectedRange: (id: string) => void;
  clearAllLines: () => void;
  handleReset: () => void;
  handelClearAll: () => void;
  splitColumns?: 1 | 2 | 3;
  sectionId: string;
  dataLayer?: string; // Data layer ID for chart type detection (e.g., 'spectrum_analysis')
  isAnySectionLoading?: boolean;
  onCancelSectionRequest?: () => void;
  isQueryParamsChanging?: boolean;
  selectedOptionFromAddSensorDropdown?: LegendSearchEntry | null;
  setSelectedOptionFromAddSensorDropdown?: Dispatch<SetStateAction<LegendSearchEntry | null>>;
  onCloseComparisonModal?: () => void;
  isMarkerSelectionMode?: boolean;
  isHarmonicSelectionMode?: boolean;
  onAddHarmonicSelection?: (selection: any) => void;
  isSideBandsSelectionMode?: boolean;
  onGenerateSideBandsFromClick?: (
    startingPoint: number,
    xAxisType: 'date' | 'numeric' | 'category',
    chartId: string
  ) => void;
  onHarmonicLineClick?: (lineId: string, position: { x: number; y: number }) => void;
  onVerticalLineClick?: (lineId: string, position: { x: number; y: number }) => void;
  focusedHarmonicColor?: string | null;
  onFocusHarmonic?: (groupId: string) => void;
  onUnfocusHarmonic?: () => void;
  selectedBenchmarkId?: string | null;
  onSetBenchmark?: (lineId: string) => void;
  selectedHarmonicLineId?: string | null;
  syncSectionLegends?: boolean;
  showPercentage?: boolean;
  onTogglePercentage?: (show: boolean) => void;
  showTableView?: boolean;
  onToggleTableView?: (show: boolean) => void;
  focusedSensor?: string | { sensorName: string; sectionId: string } | null;
  focusMode?: boolean;
  onFocus?: (sensorName: string, sectionId?: string) => void;
  onToggleFocusMode?: (sectionId: string) => void;
  // Global loading sensors state - synchronized across all sections
  loadingSensors?: Set<string>;
  setLoadingSensor?: (sensorName: string) => void;
  clearLoadingSensor?: (sensorName: string) => void;
  // Direct loading state for timestamps (works in both focus and normal modes)
  isTimestampsLoading?: boolean;
  // Pending harmonic/carrier creation state (two-step process)
  pendingHarmonicCreation?: {
    centerPoint: number;
    chartId: string;
    sectionKey: string;
    type: 'harmonic' | 'sideBands';
    labelPrefix: string;
    color: string;
    xAxisType: 'date' | 'numeric' | 'category';
    groupId: string;
  } | null;
  onCancelPendingHarmonic?: () => void;
  showDualLabels?: boolean; // Show both label name and x-value on vertical lines
  selectedMarkerLabel?: string | null; // Currently selected marker label (e.g., "T1", "T2", "M1")
  onSelectMarker?: (markerLabel: string | null) => void; // Callback when a marker is selected/clicked
  markerLabel?: string; // Custom label for marker button (e.g., "Timestamps" for sensors, "Markers" for spectrum)
  selectedTimestampsSet?: Set<string>; // Set of selected timestamp labels in focus mode (e.g., Set(["T1", "T2"])) - all should be solid and purple
  // Expandable legend series for spectrum
  expandableLegendSeries?: any[];
  expandableLegendFocusMode?: boolean; // Whether focus mode is active for expandable legend
  enabledSeries?: Set<string>; // Enabled series for filtering which traces to show (e.g., acceleration/velocity in waveform)
  popoverTimestamps?: any[];
  onToggleExpandableSeries?: (seriesId: string) => void;
  onToggleExpandableSeriesFocus?: (seriesId: string) => void;
  onToggleExpandableTimestamp?: (seriesId: string, timestampId: string) => void;
  expandableLegendLabel?: string; // Custom label for expandable legend series (e.g., "Timestamps" for spectrum)
  onRemoveMarker?: (markerId: string) => void; // Delete marker callback
  // Floating toolbar
  showFloatingToolbar?: boolean; // Show floating toolbar over charts instead of top toolbar
  showInlineFocusButton?: boolean; // Control visibility of inline "Focus" button next to each sensor (default: true)
  // Floating toolbar callbacks
  onEnterMarkerSelectionMode?: () => void;
  onEnterHarmonicSelectionMode?: () => void;
  onEnterSideBandsSelectionMode?: () => void;
  harmonicSelections?: any[];
  sideBandsSelections?: any[];
  onUpdateHarmonicSelection?: (id: string, updates: any) => void;
  onRemoveHarmonicSelection?: (id: string) => void;
  onRemoveAllHarmonics?: () => void;
  onResetHarmonic?: (id: string) => void;
  onGenerateHarmonics?: (
    startingPoint: number,
    multiplier: number,
    label: string,
    intervalType?: 'seconds' | 'minutes' | 'hours' | 'days',
    xAxisType?: 'date' | 'numeric' | 'category',
    chartId?: string
  ) => void;
  onToggleCompareModal?: (open: boolean) => void;
  onClearLines?: () => void;
  showSectionControls?: boolean;
  // Custom vertical lines
  isCustomLineMode?: string | null;
  onEnterCustomLineMode?: (groupId: string) => void;
  setCustomLineMenuState?: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      position: { x: number; y: number };
      lineData: any;
    }>
  >;
  focusedCustomGroupId?: string | null;
  thresholdVariant?: 'flat' | 'bands-fill' | 'bands-gradient';
  // Y-axis scale options
  showYAxisScale?: boolean;
  enableDynamicMargin?: boolean;
  autoscaleMode?: 'none' | 'onload' | 'onFirstDataLoad' | 'onDataLoad';
  autoscaleAxis?: 'x' | 'y' | 'both';
  // Waveform state callbacks (to expose to parent for toolbar)
  onWaveformControlsReady?: (controls: WaveformControls | null) => void;
  onWaveformPlayModeChange?: (mode: 'acceleration' | 'velocity') => void;
  waveformPlayMode?: 'acceleration' | 'velocity';
  // Waveform rendering variant: 'wavesurfer' (audio player with WaveSurfer.js) or 'plotly' (PlotlyChart based)
  waveformVariant?: 'wavesurfer' | 'plotly';
  // Waveform mode and timestamp display
  isFullWaveformMode?: boolean;
  waveformRequestedTimestamp?: string;
  waveformActualTimestamp?: string;
}

/**
 * Simplified SensorSectionChartView component that eliminates complex state management
 * and focuses on reliable data visualization.
 *
 * Key simplifications:
 * - Removed complex time range preservation logic
 * - Simplified loading state management
 * - Clear data validation without over-engineering
 * - Eliminated complex mode transition handling
 */
export const DataLayerChartView = ({
  isLoadingState,
  isFeatureLoading = false,
  componentId,
  isSplit,
  isDiscrete = false,
  showComparisonTable,
  config,
  metrics,
  data,
  customVerticalLines: customVerticalLinesProp, // Optional prop override
  enabledSensors,
  verticalLines,
  loadingLineValues = new Set(),
  intervalLines,
  sharedTimeRange,
  selectionMode,
  selectedRanges,
  handleToggleSensor,
  handleAddSensor,
  isSensorLoading,
  hasCachedDataForSensor,
  addVerticalLine,
  removeVerticalLine,
  updateVerticalLine,
  handleRangeChange,
  handleEditThresholds,
  removeIntervalLine,
  handleAddSelectedRange,
  handleRemoveSelectedRange,
  clearAllLines,
  handleReset,
  handelClearAll,
  splitColumns = 1,
  sectionId,
  dataLayer,
  isAnySectionLoading = false,
  onCancelSectionRequest,
  isQueryParamsChanging = false,
  selectedOptionFromAddSensorDropdown,
  setSelectedOptionFromAddSensorDropdown,
  onCloseComparisonModal,
  isMarkerSelectionMode,
  isHarmonicSelectionMode,
  onAddHarmonicSelection,
  isSideBandsSelectionMode,
  onGenerateSideBandsFromClick,
  onHarmonicLineClick,
  onVerticalLineClick,
  focusedHarmonicColor,
  onFocusHarmonic,
  onUnfocusHarmonic,
  selectedBenchmarkId,
  onSetBenchmark,
  selectedHarmonicLineId,
  syncSectionLegends,
  showPercentage,
  onTogglePercentage,
  showTableView,
  onToggleTableView,
  focusedSensor,
  focusMode,
  onFocus,
  onToggleFocusMode,
  loadingSensors,
  setLoadingSensor,
  clearLoadingSensor,
  isTimestampsLoading: isTimestampsLoadingProp,
  pendingHarmonicCreation,
  onCancelPendingHarmonic,
  showDualLabels = true,
  selectedMarkerLabel,
  onSelectMarker,
  markerLabel = 'Markers',
  selectedTimestampsSet,
  expandableLegendSeries = [],
  expandableLegendFocusMode = false,
  enabledSeries,
  popoverTimestamps,
  onToggleExpandableSeries,
  onToggleExpandableSeriesFocus,
  onToggleExpandableTimestamp,
  expandableLegendLabel,
  onRemoveMarker,
  showFloatingToolbar = true, // Changed to true to show the toolbar by default
  showInlineFocusButton = true,
  // Floating toolbar callbacks
  onEnterMarkerSelectionMode,
  onEnterHarmonicSelectionMode,
  onEnterSideBandsSelectionMode,
  harmonicSelections = [],
  sideBandsSelections = [],
  onUpdateHarmonicSelection,
  onRemoveHarmonicSelection,
  onRemoveAllHarmonics,
  onResetHarmonic,
  onGenerateHarmonics,
  onToggleCompareModal,
  onClearLines,
  showSectionControls,
  // Custom vertical lines
  isCustomLineMode = null,
  onEnterCustomLineMode,
  setCustomLineMenuState,
  focusedCustomGroupId,
  thresholdVariant,
  // Y-axis scale options
  showYAxisScale,
  enableDynamicMargin,
  autoscaleMode,
  autoscaleAxis,
  // Waveform callbacks
  onWaveformControlsReady,
  onWaveformPlayModeChange,
  waveformPlayMode: waveformPlayModeProp,
  waveformVariant = 'plotly', // Default to PlotlyChart visualization
  isFullWaveformMode,
  waveformRequestedTimestamp,
  waveformActualTimestamp,
}: DataLayerChartViewProps) => {
  // Extract customVerticalLines from data object (from API response) or use prop override
  const customVerticalLines = customVerticalLinesProp || data?.customVerticalLines;

  // Debug benchmark prop
  useEffect(() => {}, [selectedBenchmarkId, sectionId]);

  // Thresholds

  const { thresholdsBySensor, allThresholds, addThreshold, removeThreshold, updateThreshold } = useThresholds(
    data?.data
  );

  // Simple data validation
  const hasValidData = useMemo(() => {
    return !!(data && data.data && Array.isArray(data.data) && data.data.length > 0);
  }, [data]);

  const hasEnabledSensors = useMemo(() => {
    const result = Object.values(enabledSensors).some((enabled) => enabled === true);
    return result;
  }, [enabledSensors]);

  // Check if this is a waveform data layer (needs to be early for data validation)
  const isWaveformDataLayer = dataLayer === SENSOR_FUSION_DATA_LAYERS.WAVEFORM;

  // Get sensors with valid data
  const getValidEnabledSensorData = useCallback((inputData: any, inputEnabledSensors: Record<string, boolean>, isWaveform: boolean = false) => {
    if (!inputData?.data || !Array.isArray(inputData.data)) return [];

    return inputData.data.filter((sensorData: any) => {
      const sensorName = sensorData?.displayName || sensorData?.name;
      if (!sensorName || !inputEnabledSensors[sensorName]) return false;

      // For waveform data, check for samples array instead of x/y
      if (isWaveform) {
        const samples = sensorData.samples || sensorData.y;
        return samples && Array.isArray(samples) && samples.length > 0;
      }

      // For regular data, check x/y arrays but avoid .some() on large arrays
      const hasValidArrays =
        sensorData &&
        Array.isArray(sensorData.x) &&
        Array.isArray(sensorData.y) &&
        sensorData.x.length > 0 &&
        sensorData.y.length > 0;

      if (!hasValidArrays) return false;

      // Only check first 100 values to avoid stack overflow on large arrays
      const checkLimit = Math.min(sensorData.y.length, 100);
      for (let i = 0; i < checkLimit; i++) {
        const val = sensorData.y[i];
        if (val !== null && val !== undefined && !isNaN(Number(val))) {
          return true;
        }
      }
      return false;
    });
  }, []);

  // Group data by timestamps for Spectrum Focus Mode split view
  // In this mode, each timestamp gets its own chart with all sensors
  const groupDataByTimestamps = useCallback((inputData: any, inputEnabledSensors: Record<string, boolean>) => {
    if (!inputData?.data || !Array.isArray(inputData.data)) {
      return [];
    }

    // Find all unique timestamps and their labels from expandableLegendSeries
    const timestampGroups = new Map<string, { label: string; color: string; sensors: any[] }>();

    // Get visible timestamps from expandableLegendSeries
    if (expandableLegendSeries && expandableLegendSeries.length > 0) {
      expandableLegendSeries.forEach((series) => {
        if (series.timestamps && series.timestamps.length > 0) {
          series.timestamps
            .filter((ts: any) => ts.visible) // Only visible timestamps
            .forEach((ts: any) => {
              // CRITICAL: Use ts.timestamp (ISO string) as key, not ts.id
              // This must match sensor.chip_value which contains the ISO timestamp string
              const timestampKey = ts.timestamp || ts.id;
              if (!timestampGroups.has(timestampKey)) {
                timestampGroups.set(timestampKey, {
                  label: ts.label,
                  color: ts.color,
                  sensors: []
                });
              }
            });
        }
      });
    }

    // If no timestamps found in expandableLegendSeries, fallback to selectedTimestampsSet
    if (timestampGroups.size === 0 && selectedTimestampsSet && selectedTimestampsSet.size > 0) {
      Array.from(selectedTimestampsSet).forEach((timestampValue, index) => {
        timestampGroups.set(timestampValue, {
          label: `T${index + 1}`,
          color: `hsl(${(index * 360) / selectedTimestampsSet.size}, 70%, 50%)`,
          sensors: []
        });
      });
    }

    // Group sensor data by timestamp (chip_value)
    inputData.data.forEach((sensorData: any) => {
      const sensorName = sensorData?.displayName || sensorData?.name;
      const chipValue = sensorData.chip_value;

      if (!sensorName || !inputEnabledSensors[sensorName]) {
        return;
      }

      // Check if this sensor data has a chip_value (timestamp identifier)
      if (!chipValue) {
        return;
      }

      // Find matching timestamp group
      // chip_value should be an ISO timestamp string that directly matches timestampKey (which is now ts.timestamp)
      for (const [timestampKey, group] of timestampGroups.entries()) {
        // Try direct string comparison first (should work if both are ISO strings)
        if (chipValue === timestampKey) {
          group.sensors.push(sensorData);
          break;
        }

        // Fallback: Try normalizing both to ISO strings and compare
        try {
          const chipValueISO = new Date(chipValue).toISOString();
          const timestampKeyISO = new Date(timestampKey).toISOString();
          if (chipValueISO === timestampKeyISO) {
            group.sensors.push(sensorData);
            break;
          }
        } catch (e) {
          // Not a valid date, continue
        }
      }
    });

    // Convert map to array and filter out empty groups
    return Array.from(timestampGroups.entries())
      .map(([timestampKey, group]) => ({
        timestampKey,
        label: group.label,
        color: group.color,
        sensors: group.sensors
      }))
      .filter(group => group.sensors.length > 0);
  }, [expandableLegendSeries, selectedTimestampsSet]);

  // Valid enabled sensors with data
  // For alarm data layers, include ALL sensors regardless of enabled status
  // because alarm data is always fetched upfront and visibility is controlled client-side
  const validEnabledSensors = useMemo(() => {
    const isAlarmDataLayer = dataLayer?.includes('alarm');

    // For alarms, filter by enabled status to fix Y-axis scaling issue
    if (isAlarmDataLayer && data?.data) {
      const result: Record<string, boolean> = {};

      // Get the correct enabled sensors map (nested or flat structure)
      const sectionData = enabledSensors[sectionId];
      const alarmEnabledMap = (sectionData && typeof sectionData === 'object' && !Array.isArray(sectionData))
        ? sectionData as Record<string, boolean>
        : enabledSensors;

      data.data.forEach((sensorData: any) => {
        const name = sensorData.displayName || sensorData.name;
        // Only include if this sensor is enabled
        if (name && alarmEnabledMap[name] === true) {
          result[name] = true;
        }
      });

      return result;
    }

    // For non-alarm data layers, filter by enabled status as usual
    const validData = getValidEnabledSensorData(data, enabledSensors, isWaveformDataLayer);

    const result: Record<string, boolean> = {};

    validData.forEach((sensorData: any) => {
      const name = sensorData.displayName || sensorData.name;
      if (name) {
        result[name] = true;
      }
    });

    return result;
  }, [data, enabledSensors, isWaveformDataLayer, getValidEnabledSensorData, dataLayer, sectionId]);

  const hasValidEnabledSensorData = useMemo(() => {
    const result = Object.keys(validEnabledSensors).length > 0;
    return result;
  }, [validEnabledSensors]);

  // Check if timestamps are loading - use direct prop OR check expandable series (for focus mode)
  const isTimestampsLoading = isTimestampsLoadingProp || expandableLegendSeries?.some((series: any) => series.timestampsLoading) || false;

  // Check if any enabled sensor is currently loading (for chart loading state when switching timestamps)
  // IMPORTANT: Check BOTH loadingSensors (used by legend for spinner) AND isSensorLoading
  const isAnySensorLoading = useMemo(() => {
    // First check loadingSensors Set (this is what SensorLegend uses for the spinner)
    if (loadingSensors && loadingSensors.size > 0) {
      return true;
    }
    // Also check via isSensorLoading callback for any enabled sensor
    return Object.keys(enabledSensors)
      .filter(sensorName => enabledSensors[sensorName])
      .some(sensorName => isSensorLoading(sensorName));
  }, [enabledSensors, isSensorLoading, loadingSensors]);

  // Simplified loading logic
  // NOTE: isTimestampsLoading is checked separately - only show timestamp loading if THIS section is loading
  const isAnyKindOfLoading = isLoadingState || isFeatureLoading || isAnySectionLoading || isQueryParamsChanging || isAnySensorLoading;

  // Get value at timestamp for legend comparison table
  const getValueAtTimestamp = useCallback(
    (itemName: string, timestamp: Date | number): number | null => {
      if (!data?.data) return null;

      const sensorData = data.data.find((d: any) => d.displayName === itemName || d.name === itemName);
      if (!sensorData || !sensorData.x || !sensorData.y) return null;

      const targetTime = timestamp instanceof Date ? timestamp.getTime() : timestamp;

      // Find closest timestamp
      let closestIndex = -1;
      let minDiff = Infinity;

      for (let i = 0; i < sensorData.x.length; i++) {
        const time = sensorData.x[i] instanceof Date ? sensorData.x[i].getTime() : sensorData.x[i];
        const diff = Math.abs(time - targetTime);

        if (diff < minDiff) {
          minDiff = diff;
          closestIndex = i;
        }
      }

      if (closestIndex >= 0 && sensorData.y[closestIndex] != null) {
        return Number(sensorData.y[closestIndex]);
      }

      return null;
    },
    [data]
  );

  // Check if sensor is loading for legend
  const isSensorLoadingForLegend = useCallback(
    (itemName: string): boolean => {
      return loadingSensors?.has(itemName) || isSensorLoading(itemName);
    },
    [loadingSensors, isSensorLoading]
  );

  // Check if we have user markers for dynamic grid layout
  const hasUserMarkers = useMemo(() => {
    const userMarkers = verticalLines.filter((line: any) => !line.isHarmonic && line.markerType === 'user');
    return userMarkers.length > 0;
  }, [verticalLines]);

  // Legend colors for waveform (matching SVG design)
  const waveformLegendColors = [
    '#0ACDBA', // teal
    '#5ED3F3', // light blue
    '#FF80B5', // pink
    '#FFB28F', // peach
    '#FFD066', // yellow
  ];

  // State for selected waveform sensor
  const [selectedWaveformSensor, setSelectedWaveformSensor] = useState<string | null>(null);

  // State for waveform playback controls
  const [waveformControlsInternal, setWaveformControlsInternal] = useState<WaveformControls | null>(null);
  const [waveformPlaybackState, setWaveformPlaybackState] = useState<{ isPlaying: boolean; currentTime: number; duration: number }>({
    isPlaying: false,
    currentTime: 0,
    duration: 0,
  });
  const waveformPlayMode = waveformPlayModeProp || 'acceleration';

  // For Plotly variant, track playhead time separately (since no WaveSurfer)
  const [plotlyPlayheadTime, setPlotlyPlayheadTime] = useState(0);
  const [isPlotlyPlaying, setIsPlotlyPlaying] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const startTimeRef = useRef<number>(0);
  const pauseTimeRef = useRef<number>(0);
  const animationFrameRef = useRef<number | null>(null);
  const currentTraceIdRef = useRef<string | null>(null); // Track which trace is currently loaded

  // Calculate waveform duration based on mode
  const plotlyWaveformDuration = isFullWaveformMode ? 30 : 5;

  // Handle play/pause/stop
  useEffect(() => {
    if (!isWaveformDataLayer || waveformVariant !== 'plotly') return;
    if (!audioContextRef.current || !(audioContextRef.current as any)._waveformBuffer) return;

    const audioContext = audioContextRef.current;
    const buffer = (audioContext as any)._waveformBuffer;

    if (isPlotlyPlaying) {
      // Stop any existing playback
      if (audioSourceRef.current) {
        try {
          audioSourceRef.current.stop();
          audioSourceRef.current.disconnect();
        } catch (e) {
          // Ignore
        }
      }

      // Create and start new audio source
      const source = audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(audioContext.destination);

      // Start from current position
      const startOffset = Math.min(pauseTimeRef.current, buffer.duration - 0.01);
      source.start(0, startOffset);
      audioSourceRef.current = source;

      // Record when we started playing (actual audio context time)
      startTimeRef.current = audioContext.currentTime;

      // Animation loop to update playhead
      const updatePlayhead = () => {
        if (!isPlotlyPlaying) return;

        // Calculate current playback position:
        // pauseTimeRef.current = where we started from (offset)
        // (audioContext.currentTime - startTimeRef.current) = how much time has elapsed since we started
        const elapsed = audioContext.currentTime - startTimeRef.current;
        const currentTime = pauseTimeRef.current + elapsed;
        setPlotlyPlayheadTime(currentTime);

        // Check if we reached the end
        if (currentTime >= buffer.duration) {
          setIsPlotlyPlaying(false);
          setPlotlyPlayheadTime(0);
          pauseTimeRef.current = 0;
          startTimeRef.current = 0;
          return;
        }

        animationFrameRef.current = requestAnimationFrame(updatePlayhead);
      };

      updatePlayhead();
    } else {
      // Pause - stop audio and save position
      if (audioSourceRef.current) {
        try {
          audioSourceRef.current.stop();
          audioSourceRef.current.disconnect();
        } catch (e) {
          // Ignore
        }
        audioSourceRef.current = null;
      }

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      // Save current playback position
      if (startTimeRef.current > 0) {
        pauseTimeRef.current = pauseTimeRef.current + (audioContext.currentTime - startTimeRef.current);
      }
    }

    return () => {
      if (audioSourceRef.current) {
        try {
          audioSourceRef.current.stop();
          audioSourceRef.current.disconnect();
        } catch (e) {
          // Ignore
        }
        audioSourceRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isWaveformDataLayer, waveformVariant, isPlotlyPlaying]);

  // Create synthetic waveform controls for Plotly variant
  // This allows the toolbar's editable timestamp to work
  useEffect(() => {
    if (!isWaveformDataLayer || waveformVariant !== 'plotly') return;

    // Format time as MM:SS.ms
    const formatTime = (seconds: number) => {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      const ms = Math.floor((seconds % 1) * 100);
      return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
    };

    // Create synthetic controls object for Plotly variant
    const syntheticControls: WaveformControls = {
      play: () => setIsPlotlyPlaying(true),
      pause: () => setIsPlotlyPlaying(false),
      stop: () => {
        setIsPlotlyPlaying(false);
        setPlotlyPlayheadTime(0);
        pauseTimeRef.current = 0;
        startTimeRef.current = 0;
      },
      zoomIn: () => {},
      zoomOut: () => {},
      resetZoom: () => {},
      seekTo: (timeInSeconds: number) => {
        const clampedTime = Math.max(0, Math.min(timeInSeconds, plotlyWaveformDuration));
        setPlotlyPlayheadTime(clampedTime);
        pauseTimeRef.current = clampedTime;
        startTimeRef.current = 0;
        // If playing, restart from new position
        if (isPlotlyPlaying) {
          setIsPlotlyPlaying(false);
          setTimeout(() => setIsPlotlyPlaying(true), 50);
        }
      },
      isReady: true,
      isPlaying: isPlotlyPlaying,
      currentTime: plotlyPlayheadTime,
      duration: plotlyWaveformDuration,
      zoomLevel: 1,
      formatTime,
      loopRegion: null,
      isLooping: false,
      setLoopRegion: () => {},
      toggleLoop: () => {},
      clearLoopRegion: () => {},
      interactionMode: 'handlebars' as const,
      setInteractionMode: () => {},
      toggleInteractionMode: () => {},
    };

    onWaveformControlsReady?.(syntheticControls);
  }, [isWaveformDataLayer, waveformVariant, plotlyPlayheadTime, isPlotlyPlaying, plotlyWaveformDuration, onWaveformControlsReady]);

  // Update waveformPlaybackState when Plotly playback changes
  // This ensures opacity changes are triggered in waveformTraces
  useEffect(() => {
    if (!isWaveformDataLayer || waveformVariant !== 'plotly') return;

    setWaveformPlaybackState({
      isPlaying: isPlotlyPlaying,
      currentTime: plotlyPlayheadTime,
      duration: plotlyWaveformDuration,
    });
  }, [isWaveformDataLayer, waveformVariant, isPlotlyPlaying, plotlyPlayheadTime, plotlyWaveformDuration]);

  // Notify parent when waveform controls change
  const handleWaveformControlsReady = useCallback((controls: WaveformControls | null) => {
    setWaveformControlsInternal(controls);
    // Merge with current playback state for parent
    if (controls) {
      onWaveformControlsReady?.({
        ...controls,
        ...waveformPlaybackState,
      });
    } else {
      onWaveformControlsReady?.(null);
    }
  }, [onWaveformControlsReady, waveformPlaybackState]);

  // Handle playback state changes (time updates during playback)
  const handleWaveformPlaybackStateChange = useCallback((state: { isPlaying: boolean; currentTime: number; duration: number }) => {
    setWaveformPlaybackState(state);
    // Update parent with merged controls + state
    if (waveformControlsInternal) {
      onWaveformControlsReady?.({
        ...waveformControlsInternal,
        ...state,
      });
    }
  }, [waveformControlsInternal, onWaveformControlsReady]);

  // Use internal state but sync with parent via callback
  const waveformControls = waveformControlsInternal;

  // Convert data to WaveSurfer traces format for waveform data layer
  // Each sensor has TWO waveforms: acceleration and velocity
  // The active mode (waveformPlayMode) shows at full opacity, inactive at reduced opacity
  const waveformTraces = useMemo((): WaveformTraceData[] => {
    // console.log('[Waveform Debug] isWaveformDataLayer:', isWaveformDataLayer, 'data:', data, 'data?.data:', data?.data?.length);
    if (!isWaveformDataLayer || !data?.data) return [];

    const enabledSensorNames = Object.keys(enabledSensors).filter(name => enabledSensors[name]);

    // Determine which single sensor to show:
    // 1. If selectedWaveformSensor is set, use it
    // 2. If focusedSensor is set and enabled, use it
    // 3. Otherwise use the first enabled sensor
    // 4. If no sensors enabled, use first from data
    let targetSensor: string | null = null;

    // Get focusedSensor name (handle both string and object types)
    const focusedSensorName = typeof focusedSensor === 'string' ? focusedSensor : focusedSensor?.sensorName;

    if (selectedWaveformSensor && data.data.some((s: any) => (s.displayName || s.name) === selectedWaveformSensor)) {
      targetSensor = selectedWaveformSensor;
    } else if (focusedSensorName && enabledSensorNames.includes(focusedSensorName)) {
      targetSensor = focusedSensorName;
    } else if (enabledSensorNames.length > 0) {
      targetSensor = enabledSensorNames[0];
    } else if (data.data.length > 0) {
      targetSensor = data.data[0].displayName || data.data[0].name;
    }

    if (!targetSensor) {
      // console.log('[Waveform Debug] No targetSensor found');
      return [];
    }

    // Find the sensor data for the target sensor
    const sensorData = data.data.find((s: any) =>
      (s.displayName || s.name) === targetSensor
    );

    if (!sensorData) {
      // console.log('[Waveform Debug] No sensorData found for:', targetSensor);
      return [];
    }

    // Get acceleration and velocity samples from recordings array
    // Each sensor should have separate acceleration and velocity recordings
    const recordings = sensorData.recordings || [];
    // console.log('[Waveform Debug] recordings:', recordings.length, 'sensorData.samples:', sensorData.samples?.length, 'sensorData.y:', sensorData.y?.length);
    const accelerationRecording = recordings.find((r: any) => r.type === 'acceleration');
    const velocityRecording = recordings.find((r: any) => r.type === 'velocity');

    // Get samples - each type should have its own distinct data
    const accelerationSamples = accelerationRecording?.previewSamples || sensorData.samples || sensorData.y || [];
    // console.log('[Waveform Debug] accelerationSamples:', accelerationSamples.length, 'velocityRecording:', !!velocityRecording);

    // For velocity, use recording data if available, otherwise generate mock velocity data
    // This ensures we always have distinct velocity vs acceleration visualization
    let velocitySamples = velocityRecording?.previewSamples || [];

    // If no velocity data but we have acceleration data, generate synthetic velocity
    // Velocity is smoother/lower frequency than acceleration
    if (velocitySamples.length === 0 && accelerationSamples.length > 0) {
      const sampleRate = sensorData.sampleRate || 4000;
      const duration = accelerationSamples.length / sampleRate;
      const hash = targetSensor.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      const baseFrequency = (20 + (hash % 30)) * 0.2; // Much lower frequency for velocity

      velocitySamples = new Array(accelerationSamples.length);
      for (let i = 0; i < accelerationSamples.length; i++) {
        const t = i / sampleRate;
        // Smooth sine wave - very different from spiky acceleration
        let value = Math.sin(2 * Math.PI * baseFrequency * t);
        // Slow amplitude modulation
        const envelope = 0.85 + 0.15 * Math.sin(2 * Math.PI * 0.5 * t);
        value *= envelope;
        // Subtle harmonic
        value += 0.08 * Math.sin(2 * Math.PI * baseFrequency * 2 * t + 0.3);
        velocitySamples[i] = Math.max(-1, Math.min(1, value * 0.7));
      }
    }

    const traces: WaveformTraceData[] = [];

    // Fixed colors for Acceleration and Velocity - using timestamp palette (must match Waveform.tsx)
    const ACCELERATION_COLOR = '#5ed3f3'; // Light cyan (rawColorPalette index 0)
    const VELOCITY_COLOR = '#6667dd'; // Purple (rawColorPalette index 1)

    // Add acceleration trace - uses fixed color
    // Only add if enabledSeries is not set, is empty, or includes 'acceleration'
    const showAcceleration = !enabledSeries || enabledSeries.size === 0 || enabledSeries.has('acceleration');
    // Determine opacity based on playback state - when playing, non-active mode is dimmed
    const isPlaying = waveformPlaybackState.isPlaying;

    if (accelerationSamples.length > 0 && showAcceleration) {
      // Full opacity when: not playing, OR playing acceleration
      // Dimmed when: playing velocity
      const accOpacity = isPlaying && waveformPlayMode === 'velocity' ? 0.3 : 1;
      traces.push({
        id: `${targetSensor}-acceleration`,
        name: `${targetSensor} (Acceleration)`,
        data: accelerationSamples,
        color: ACCELERATION_COLOR,
        sampleRate: accelerationRecording?.sampleRate || sensorData.sampleRate || 4000,
        visible: true,
        opacity: accOpacity,
        type: 'acceleration',
      });
    }

    // Add velocity trace - uses fixed color (visually distinct from acceleration)
    // Only add if enabledSeries is not set, is empty, or includes 'velocity'
    const showVelocity = !enabledSeries || enabledSeries.size === 0 || enabledSeries.has('velocity');
    if (velocitySamples.length > 0 && showVelocity) {
      // Full opacity when: not playing, OR playing velocity
      // Dimmed when: playing acceleration
      const velOpacity = isPlaying && waveformPlayMode === 'acceleration' ? 0.3 : 1;
      traces.push({
        id: `${targetSensor}-velocity`,
        name: `${targetSensor} (Velocity)`,
        data: velocitySamples,
        color: VELOCITY_COLOR,
        sampleRate: velocityRecording?.sampleRate || sensorData.sampleRate || 4000,
        visible: true,
        opacity: velOpacity,
        type: 'velocity',
      });
    }

    // console.log('[Waveform Debug] Final traces:', traces.length, traces.map(t => t.name));
    return traces;
  }, [isWaveformDataLayer, data, enabledSensors, metrics, focusedSensor, selectedWaveformSensor, waveformPlayMode, enabledSeries, waveformPlaybackState.isPlaying]);

  // Create invisible audio player using Web Audio API
  // This syncs with the selected waveform trace (acceleration/velocity) and timestamp
  useEffect(() => {
    if (!isWaveformDataLayer || waveformVariant !== 'plotly') return;
    if (waveformTraces.length === 0) return;

    // Get the first visible trace (acceleration or velocity) based on current selection
    const firstTrace = waveformTraces[0];
    if (!firstTrace?.data || firstTrace.data.length === 0) return;

    // Check if this is the same trace - don't recreate buffer if it hasn't changed
    const traceId = `${firstTrace.name}-${firstTrace.data.length}-${isFullWaveformMode}`;
    if (currentTraceIdRef.current === traceId) {
      return; // Same trace, don't recreate buffer
    }
    currentTraceIdRef.current = traceId;

    // Initialize AudioContext
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    const audioContext = audioContextRef.current;
    const sampleRate = firstTrace.sampleRate || 4000;
    const samples = firstTrace.data;

    // Create AudioBuffer from waveform data
    const audioBuffer = audioContext.createBuffer(1, samples.length, sampleRate);
    const channelData = audioBuffer.getChannelData(0);

    // Normalize and copy samples
    const maxValue = Math.max(...samples.map(Math.abs));
    for (let i = 0; i < samples.length; i++) {
      channelData[i] = samples[i] / (maxValue || 1);
    }

    // Store buffer for playback
    (audioContext as any)._waveformBuffer = audioBuffer;

    // Stop current playback when changing tracks (only when actually changing, not on re-render)
    if (isPlotlyPlaying) {
      setIsPlotlyPlaying(false);
      setPlotlyPlayheadTime(0);
      pauseTimeRef.current = 0;
    }

    return () => {
      // Cleanup
      if (audioSourceRef.current) {
        try {
          audioSourceRef.current.stop();
          audioSourceRef.current.disconnect();
        } catch (e) {
          // Ignore
        }
        audioSourceRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isWaveformDataLayer, waveformVariant, waveformTraces, isFullWaveformMode]);

  // All waveform traces for the legend (shows all sensors, not just the active one)
  const allWaveformTraces = useMemo((): WaveformTraceData[] => {
    if (!isWaveformDataLayer || !data?.data) return [];

    const metric = metrics[0];
    const activeTraceId = waveformTraces[0]?.id;

    return data.data.map((sensorData: any, index: number) => {
      const sensorName = sensorData.displayName || sensorData.name;
      const samples = sensorData.samples || sensorData.y || [];
      const sensorColor = metric?.sensorColors?.[sensorName] || waveformLegendColors[index % waveformLegendColors.length];
      const isEnabled = enabledSensors[sensorName] !== false;

      return {
        id: sensorName,
        name: sensorName,
        data: samples,
        color: sensorColor,
        sampleRate: sensorData.sampleRate || 4000,
        visible: sensorName === activeTraceId,
        opacity: isEnabled ? 1 : 0.5,
      };
    });
  }, [isWaveformDataLayer, data, metrics, enabledSensors, waveformTraces]);

  // Convert waveform data to PlotlyChart-compatible format (for 'plotly' variant)
  // Shows ALL enabled traces (acceleration and/or velocity based on enabledSeries)
  const waveformPlotlyData = useMemo(() => {
    if (!isWaveformDataLayer || !data?.data || waveformTraces.length === 0) {
      return { data: [] };
    }

    // Convert all waveform traces to PlotlyChart format
    // Use numeric seconds for x-axis (0, 0.00025, 0.0005, ..., 5.0)
    const plotlyTraces = waveformTraces
      .filter(trace => trace.data && trace.data.length > 0)
      .map(trace => {
        const sampleRate = trace.sampleRate || 4000;
        // Convert sample index to numeric seconds
        const xValues = trace.data.map((_: number, i: number) => {
          return i / sampleRate;
        });

        return {
          displayName: trace.name,
          name: trace.name,
          x: xValues,
          y: trace.data,
          color: trace.color,
          opacity: trace.opacity, // Pass opacity to PlotlyChart
        };
      });

    // DEBUG: Log the plotly data structure
    // console.log('[Waveform PlotlyData DEBUG] plotlyTraces:', plotlyTraces.length, 'first trace x sample:', plotlyTraces[0]?.x?.slice(0, 3), 'first trace y sample:', plotlyTraces[0]?.y?.slice(0, 3), 'first trace x length:', plotlyTraces[0]?.x?.length);

    return {
      data: plotlyTraces
    };
  }, [isWaveformDataLayer, data, waveformTraces]);

  // Handle waveform trace selection from legend - only ONE sensor at a time
  const handleWaveformTraceSelect = useCallback((traceId: string) => {
    setSelectedWaveformSensor(traceId);
  }, []);

  // Custom toggle for waveform - selects sensor instead of toggling
  // For waveform, clicking a sensor in the legend should SELECT it, not toggle
  const handleWaveformSensorToggle = useCallback((sensorName: string) => {
    setSelectedWaveformSensor(sensorName);
  }, []);

  // For waveform, only ONE sensor is enabled at a time (the selected one)
  // Other sensors are shown but clicking them will select them (replacing the current selection)
  const waveformEnabledSensors = useMemo(() => {
    if (!isWaveformDataLayer) return enabledSensors;

    const result: Record<string, boolean> = {};

    // Get the currently selected sensor name from traces
    const selectedSensorName = waveformTraces[0]?.name?.split(' (')[0] || null;

    // Only the selected sensor is "enabled" (shown as active)
    if (data?.data) {
      data.data.forEach((sensorData: any) => {
        const sensorName = sensorData.displayName || sensorData.name;
        // Only the selected sensor shows as enabled
        result[sensorName] = sensorName === selectedSensorName;
      });
    }

    return result;
  }, [isWaveformDataLayer, enabledSensors, data, waveformTraces]);

  // Track which sensor is currently selected for waveform display
  // Extract sensor name from trace ID (e.g., "Drive 1 Gearbox 10H-acceleration" -> "Drive 1 Gearbox 10H")
  const selectedWaveformSensorId = useMemo(() => {
    const traceId = waveformTraces[0]?.id;
    if (!traceId) return null;
    // Remove -acceleration or -velocity suffix to get sensor name
    return traceId.replace(/-acceleration$/, '').replace(/-velocity$/, '');
  }, [waveformTraces]);

  // Convert verticalLines to WaveformMarker format for waveform data layer
  const waveformMarkers = useMemo((): WaveformMarker[] => {
    if (!isWaveformDataLayer) return [];

    // Count user markers to determine which should be dashed
    let userMarkerIndex = 0;

    return verticalLines.map((line: any) => {
      // Try to get the time value from various possible properties
      // For waveform, time is stored as a number in timestamp or xValue
      let timeValue = 0;
      if (typeof line.timestamp === 'number') {
        timeValue = line.timestamp;
      } else if (typeof line.xValue === 'number') {
        timeValue = line.xValue;
      } else if (typeof line.x === 'number') {
        timeValue = line.x;
      }

      // Use the label from the line (e.g., "M1", "M2" for user markers, "H0", "H1" for harmonics)
      const label = line.label || (line.isHarmonic ? `H${line.harmonicNumber || 0}` : `${timeValue.toFixed(2)}s`);

      // Color based on marker type - FORCE black for user markers
      let markerColor: string;
      if (line.markerType === 'user') {
        markerColor = '#000000'; // Always black for user markers
      } else if (line.isHarmonic) {
        markerColor = line.color || '#22c55e'; // Green for harmonics
      } else {
        markerColor = line.color || '#6b7280'; // Gray for system markers
      }

      // User markers: first is solid, subsequent are dashed
      let isDashed = false;
      if (line.markerType === 'user') {
        isDashed = userMarkerIndex > 0; // First marker (index 0) is solid, others are dashed
        userMarkerIndex++;
      }

      return {
        id: line.id || `marker-${timeValue}`,
        time: timeValue,
        label,
        color: markerColor,
        isHarmonic: line.isHarmonic || false,
        groupId: line.groupId,
        isDashed,
      };
    });
  }, [isWaveformDataLayer, verticalLines]);

  // Handle adding markers on waveform click
  const handleWaveformAddMarker = useCallback((time: number) => {
    // For waveform, we use addVerticalLine with the time value
    // The time is in seconds, we pass it as the xValue
    // We pass markerType: 'user' to get proper "M1, M2" labels
    addVerticalLine(
      time, // timestamp (using time as numeric value)
      {}, // values (empty for waveform markers)
      time, // xValue - the actual time position
      undefined, // customLabel
      undefined, // customColor
      false, // isHarmonic
      false, // isFirstHarmonic
      undefined, // chartId
      undefined, // sectionKey
      'user' // markerType - this generates "M1, M2, M3..." labels
    );
  }, [addVerticalLine]);

  // Simple sensor legend renderer
  const renderSensorLegend = useCallback(
    (sensorColors: Record<string, string>) => {
      // Filter user markers for table mode
      const userMarkers = verticalLines.filter((line: any) => !line.isHarmonic && line.markerType === 'user');

      // For waveform, use special enabled sensors (all clickable) and toggle handler
      const legendSensors = isWaveformDataLayer ? waveformEnabledSensors : enabledSensors;
      const legendToggleHandler = isWaveformDataLayer ? handleWaveformSensorToggle : handleToggleSensor;

      // For waveform, show which sensor is currently selected/focused
      const legendFocusedSensor = isWaveformDataLayer ? selectedWaveformSensorId : focusedSensor;
      const legendFocusMode = isWaveformDataLayer ? true : focusMode; // Waveform always in "focus" mode (one sensor active)

      return (
        <SensorLegend
          title=""
          sensors={legendSensors}
          sensorColors={sensorColors}
          onToggleSensor={legendToggleHandler}
          onAddSensor={handleAddSensor}
          onReset={handleReset}
          onClearAll={handelClearAll}
          sectionType={sectionId}
          data={data}
          isLoading={isAnyKindOfLoading}
          selectedOptionFromAddSensorDropdown={selectedOptionFromAddSensorDropdown}
          setSelectedOptionFromAddSensorDropdown={setSelectedOptionFromAddSensorDropdown}
          syncSectionLegends={syncSectionLegends}
          focusMode={legendFocusMode}
          focusedSensor={legendFocusedSensor}
          onFocus={onFocus}
          onToggleFocusMode={onToggleFocusMode}
          loadingSensors={loadingSensors}
          setLoadingSensor={setLoadingSensor}
          markers={userMarkers}
          benchmarkMarkerId={selectedBenchmarkId}
          onSetBenchmark={onSetBenchmark}
          showPercentage={showPercentage}
          onTogglePercentage={onTogglePercentage}
          showTableView={showTableView}
          onToggleTableView={onToggleTableView}
          selectedMarkerLabel={selectedMarkerLabel}
          onSelectMarker={onSelectMarker}
          onRemoveMarker={onRemoveMarker || removeVerticalLine}
          expandableLegendSeries={expandableLegendSeries}
          expandableLegendFocusMode={expandableLegendFocusMode}
          popoverTimestamps={popoverTimestamps}
          onToggleExpandableSeries={onToggleExpandableSeries}
          onToggleExpandableSeriesFocus={onToggleExpandableSeriesFocus}
          onToggleExpandableTimestamp={onToggleExpandableTimestamp}
          markerLabel={markerLabel}
          expandableLegendLabel={expandableLegendLabel}
          showInlineFocusButton={showInlineFocusButton}
          showSectionControls={showSectionControls}
          hasCachedDataForSensor={hasCachedDataForSensor}
        />
      );
    },
    [
      enabledSensors,
      handleToggleSensor,
      handleAddSensor,
      handleReset,
      handelClearAll,
      sectionId,
      data,
      isAnyKindOfLoading,
      selectedOptionFromAddSensorDropdown,
      setSelectedOptionFromAddSensorDropdown,
      syncSectionLegends,
      focusMode,
      focusedSensor,
      onFocus,
      onToggleFocusMode,
      loadingSensors,
      setLoadingSensor,
      verticalLines,
      selectedBenchmarkId,
      onSetBenchmark,
      showPercentage,
      onTogglePercentage,
      showTableView,
      onToggleTableView,
      selectedMarkerLabel,
      onSelectMarker,
      removeVerticalLine,
      expandableLegendSeries,
      expandableLegendFocusMode,
      popoverTimestamps,
      onToggleExpandableSeries,
      onToggleExpandableSeriesFocus,
      onToggleExpandableTimestamp,
      markerLabel,
      expandableLegendLabel,
      showInlineFocusButton,
      hasCachedDataForSensor,
      onRemoveMarker,
      isWaveformDataLayer,
      waveformEnabledSensors,
      handleWaveformSensorToggle,
      selectedWaveformSensorId,
    ]
  );

  // Simple comparison table modal renderer
  const renderComparisonModal = useCallback(() => {
    if (!metrics.length) return null;

    // Create loading sensors set based on current loading state
    const loadingSensorsSet = new Set<string>();
    config.sensorUiNames.forEach((sensorName: string) => {
      if (isSensorLoading(sensorName)) {
        loadingSensorsSet.add(`${metrics[0].key}-${sensorName}`);
      }
    });

    return (
      <ComparisonTableModal
        {...({
        open: showComparisonTable,
        onClose: () => {
          // Call the parent handler to close the modal
          onCloseComparisonModal?.();
        },
        sensors: config.sensorUiNames,
        verticalLines,
        enabledSensors,
        onToggleSensor: handleToggleSensor,
        onRemoveVerticalLine: removeVerticalLine,
        onClearAllLines: clearAllLines,
        sensorColors: metrics[0].sensorColors,
        sectionType: metrics[0].key,
        sectionName: metrics[0].label,
        unit: metrics[0].unit,
        onAddSensor: handleAddSensor,
        loadingSensors: loadingSensorsSet,
        dataTypes: metrics.filter((m) => m.show).map((m) => m.label),
        selectedBenchmarkId,
        onSetBenchmark,
        onUpdateVerticalLine: updateVerticalLine,
        focusedHarmonicColor,
        onFocusHarmonic,
        onUnfocusHarmonic,
        // Chart props - pass everything for full synchronization
        data,
        addVerticalLine,
        sharedTimeRange,
        onRangeChange: handleRangeChange,
        onEditThresholds: handleEditThresholds,
        thresholds: allThresholds,
        intervalLines,
        onRemoveIntervalLine: removeIntervalLine,
        selectionMode,
        selectedRanges,
        onAddSelectedRange: handleAddSelectedRange,
        onRemoveSelectedRange: handleRemoveSelectedRange,
        isMarkerSelectionMode,
        isHarmonicSelectionMode,
        onAddHarmonicSelection,
        isSideBandsSelectionMode,
        onGenerateSideBandsFromClick,
        onHarmonicLineClick,
        onVerticalLineClick,
        selectedHarmonicLineId,
        sectionId,
        dataLayer,
        pendingHarmonicCreation,
        onCancelPendingHarmonic,
        showDualLabels,
        showPercentage,
        onTogglePercentage
      } as any)}
      />
    );
  }, [
    showComparisonTable,
    metrics,
    config.sensorUiNames,
    verticalLines,
    enabledSensors,
    handleToggleSensor,
    removeVerticalLine,
    clearAllLines,
    handleAddSensor,
    isSensorLoading,
    focusedHarmonicColor,
    onFocusHarmonic,
    onUnfocusHarmonic,
    selectedBenchmarkId,
    onSetBenchmark,
    updateVerticalLine,
    onCloseComparisonModal,
    data,
    addVerticalLine,
    sharedTimeRange,
    handleRangeChange,
    handleEditThresholds,
    allThresholds,
    intervalLines,
    removeIntervalLine,
    selectionMode,
    selectedRanges,
    handleAddSelectedRange,
    handleRemoveSelectedRange,
    isMarkerSelectionMode,
    isHarmonicSelectionMode,
    onAddHarmonicSelection,
    isSideBandsSelectionMode,
    onGenerateSideBandsFromClick,
    onHarmonicLineClick,
    onVerticalLineClick,
    selectedHarmonicLineId,
    sectionId,
    dataLayer,
    pendingHarmonicCreation,
    onCancelPendingHarmonic,
    showDualLabels,
    showPercentage,
    onTogglePercentage,
  ]);

  // Get sensor color helper
  const getSensorColor = useCallback((sensorName: string, sensorColors: Record<string, string>, index: number) => {
    return sensorColors[sensorName] || `hsl(${(index * 360) / 10}, 100%, 50%)`;
  }, []);

  // Global Y range for combined charts
  const getGlobalYRange = useCallback(
    (metricKey: string): [number, number] | null => {
      // Skip for waveform data layer - WaveSurfer handles its own scaling
      if (isWaveformDataLayer || isSplit || !hasValidData) return null;

      let minY = Number.POSITIVE_INFINITY;
      let maxY = Number.NEGATIVE_INFINITY;

      data?.data.forEach((sensorData: any) => {
        if (!sensorData.y || !Array.isArray(sensorData.y)) return;

        const sensorName = sensorData.displayName;
        if (!validEnabledSensors[sensorName]) return;

        // Use loop instead of spread to avoid stack overflow with large arrays
        for (let i = 0; i < sensorData.y.length; i++) {
          const val = sensorData.y[i];
          if (typeof val === 'number' && isFinite(val)) {
            if (val < minY) minY = val;
            if (val > maxY) maxY = val;
          }
        }
      });

      if (minY !== Number.POSITIVE_INFINITY && maxY !== Number.NEGATIVE_INFINITY) {
        const padding = Math.max((maxY - minY) * 0.1, 1);
        return [minY - padding, maxY + padding];
      }

      return null;
    },
    [isWaveformDataLayer, isSplit, hasValidData, data, validEnabledSensors]
  );

  // Show loading state
  // Also show loading when timestamps are loading in focus mode (even if we have existing data)
  // Also show loading when any sensor is loading (switching timestamps)
  // Show loading if:
  // 1. Section is loading AND has no valid data yet, OR
  // 2. Timestamps are loading AND section is actually loading (not just passively waiting), OR
  // 3. Any sensor in this section is actively loading
  const shouldShowLoading = (isAnyKindOfLoading && !hasValidEnabledSensorData) ||
                            (isTimestampsLoading && isLoadingState) ||
                            isAnySensorLoading;

  if (shouldShowLoading) {
    return (
      <div className="grid gap-4 grid-cols-[auto_1fr]">
        <div>{metrics.length > 0 && renderSensorLegend(metrics[0].sensorColors)}</div>
        <div className="min-w-0">
          <div className="space-y-4">
            {isSplit ? (
              <div
                className={clsx(
                  'grid gap-4',
                  splitColumns === 1
                    ? 'grid-cols-1'
                    : splitColumns === 2
                    ? 'grid-cols-1 lg:grid-cols-2'
                    : 'grid-cols-1 lg:grid-cols-3'
                )}
              >
                {[...Array(splitColumns > 1 ? 6 : 3)].map((_, idx) => (
                  <ChartLoader key={idx} height={splitColumns > 1 ? 180 : 300} />
                ))}
              </div>
            ) : (
              <ChartLoader height={300} />
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show no sensors state
  // Exception: For alarms, don't show empty state - render chart with no bars visible
  // This keeps the legend functional for toggling alarms back on
  const isAlarmDataLayer = dataLayer?.includes('alarm');

  if (!isAnyKindOfLoading && !hasEnabledSensors && !isAlarmDataLayer) {
    return (
      <div className="grid gap-4 grid-cols-[auto_1fr]">
        <div>{metrics.length > 0 && renderSensorLegend(metrics[0].sensorColors)}</div>
        <div className="min-w-0">
          <ChartEmptyState type={EMPTY_STATE_ENUM.NO_SENSORS} />
        </div>
      </div>
    );
  }

  // Show no data state
  // Exception: For alarms, bypass this check - data is always valid when it exists

  if (!isAnyKindOfLoading && !hasValidEnabledSensorData && hasEnabledSensors && !isAlarmDataLayer) {
    return (
      <div className="grid gap-4 grid-cols-[auto_1fr]">
        <div>{metrics.length > 0 && renderSensorLegend(metrics[0].sensorColors)}</div>
        <div className="min-w-0">
          <ChartEmptyState type={EMPTY_STATE_ENUM.NO_DATA} />
        </div>
      </div>
    );
  }

  // Render charts - COMPREHENSIVE LOGGING
  // console.log('[DataLayerChartView] ===== RENDERING =====');
  // console.log('[DataLayerChartView] sectionId:', sectionId, '(type:', typeof sectionId, ')');
  // console.log('[DataLayerChartView] dataLayer:', dataLayer, '(type:', typeof dataLayer, ')');
  // console.log('[DataLayerChartView] isSplit:', isSplit, '(type:', typeof isSplit, ')');
  // console.log('[DataLayerChartView] focusMode:', focusMode, '(type:', typeof focusMode, ')');
  // console.log('[DataLayerChartView] expandableLegendFocusMode:', expandableLegendFocusMode, '(type:', typeof expandableLegendFocusMode, ')');
  // console.log('[DataLayerChartView] selectedTimestampsSet:', selectedTimestampsSet);
  // console.log('[DataLayerChartView] expandableLegendSeries:', expandableLegendSeries);
  // console.log('[DataLayerChartView] metrics:', metrics.map(m => m.key));
  // console.log('[DataLayerChartView] data keys:', data ? Object.keys(data) : 'no data');
  // console.log('[DataLayerChartView] data.data length:', data?.data?.length);
  // console.log('[DataLayerChartView] enabledSensors:', Object.keys(enabledSensors).filter(k => enabledSensors[k]));
  // console.log('[DataLayerChartView] =====================');

  return (
    <div className="grid gap-4 grid-cols-[auto_1fr]">
      {/* Sidebar */}
      <div>{metrics.length > 0 && renderSensorLegend(metrics[0].sensorColors)}</div>

      {/* Charts */}
      <div className="space-y-6 min-w-0" data-testid={`graphs_container_${generateTestId(sectionId)}`}>
        {metrics.map((metric) =>
          metric.show ? (
            isSplit ? (
              (() => {
                // For waveform, use WaveSurfer player (no split mode, always combined)
                if (isWaveformDataLayer) {
                  return null; // Waveform is rendered below in the combined section
                }

                // CRITICAL: In Spectrum Focus Mode, split by TIMESTAMPS instead of SENSORS
                // Each timestamp gets its own chart with all sensors
                // console.log('[Split Mode Check] metric.key:', metric.key);
                // console.log('[Split Mode Check] sectionId:', sectionId);
                // console.log('[Split Mode Check] dataLayer:', dataLayer);
                // console.log('[Split Mode Check] SPECTRUM_ANALYSIS:', SENSOR_FUSION_DATA_LAYERS.SPECTRUM_ANALYSIS);
                // console.log('[Split Mode Check] focusMode:', focusMode);
                // console.log('[Split Mode Check] expandableLegendFocusMode:', expandableLegendFocusMode);

                const isSpectrumFocusMode =
                  dataLayer === SENSOR_FUSION_DATA_LAYERS.SPECTRUM_ANALYSIS &&
                  expandableLegendFocusMode;

                // console.log('[Split Mode Check] isSpectrumFocusMode:', isSpectrumFocusMode);

                if (isSpectrumFocusMode) {
                  // console.log('[Split Mode] ENTERING SPECTRUM FOCUS MODE SPLIT');
                  // Split by timestamps - each timestamp in its own chart
                  const timestampGroups = groupDataByTimestamps(data, enabledSensors);

                  // console.log('[Spectrum Focus Split] Timestamp groups:', timestampGroups);
                  // console.log('[Spectrum Focus Split] expandableLegendSeries:', expandableLegendSeries);
                  // console.log('[Spectrum Focus Split] selectedTimestampsSet:', selectedTimestampsSet);
                  // console.log('[Spectrum Focus Split] data.data:', data?.data);

                  if (timestampGroups.length === 0) {
                    return (
                      <div key={metric.key} className="col-span-10">
                        <ChartEmptyState
                          type={EMPTY_STATE_ENUM.NO_DATA}
                          className={splitColumns > 2 ? 'max-h-[180px]' : ''}
                        />
                      </div>
                    );
                  }

                  return (
                    <div key={metric.key}>
                      <div
                        className={`grid gap-4 ${
                          splitColumns === 1
                            ? 'grid-cols-1'
                            : splitColumns === 2
                            ? 'grid-cols-1 lg:grid-cols-2'
                            : 'grid-cols-1 lg:grid-cols-3'
                        }`}
                      >
                        {timestampGroups.map((group) => {
                          // Create a synthetic data object with all sensors for this timestamp
                          const timestampData = {
                            data: group.sensors
                          };

                          // Get colors for all sensors in this timestamp group
                          const sensorColors = group.sensors.map((s: any, idx: number) =>
                            getSensorColor(s.displayName || s.name, metric.sensorColors, idx)
                          );
                          const sensorLabels = group.sensors.map((s: any) => s.displayName || s.name);
                          const sensorEnabledMap = group.sensors.reduce((acc: any, s: any) => {
                            acc[s.displayName || s.name] = true;
                            return acc;
                          }, {});

                          return (
                            <div
                              key={`${metric.key}-${group.timestampKey}`}
                              className="relative bg-[#f0f2f7] rounded"
                              data-testid={`graph_split_${generateTestId(metric.key)}_${generateTestId(group.label)}`}
                            >
                              <PlotlyChart
                                data={timestampData}
                                title={group.label} // "T1", "T2", etc.
                                unit={metric.unit}
                                isDiscrete={isDiscrete}
                                height={isDiscrete ? 200 : 300}
                                colors={sensorColors}
                                labels={sensorLabels}
                                enabledSensors={sensorEnabledMap}
                                verticalLines={verticalLines}
                                customVerticalLines={data?.customVerticalLines || customVerticalLines}
                                thresholds={allThresholds}
                                chartType={metric.key}
                                sectionId={sectionId}
                                dataLayer={dataLayer}
                                chartId={`${metric.key}-${group.label.toLowerCase().replace(/\s+/g, '-')}`}
                                sharedTimeRange={sharedTimeRange}
                                onRangeChange={handleRangeChange}
                                onEditThresholds={() => handleEditThresholds(metric.key)}
                                showThresholds={true}
                                thresholdVariant={thresholdVariant}
                                intervalLines={intervalLines}
                                onRemoveIntervalLine={removeIntervalLine}
                                selectionMode={selectionMode}
                                selectedRanges={selectedRanges}
                                onAddSelectedRange={handleAddSelectedRange}
                                onRemoveSelectedRange={handleRemoveSelectedRange}
                                onAddVerticalLine={addVerticalLine}
                                onRemoveVerticalLine={removeVerticalLine}
                                onUpdateVerticalLine={updateVerticalLine}
                                globalYRange={null}
                                showUnits={isSplit}
                                isComparisonEnabled={true}
                                splitColumns={splitColumns}
                                isMarkerSelectionMode={isMarkerSelectionMode}
                                isHarmonicSelectionMode={isHarmonicSelectionMode}
                                onAddHarmonicSelection={onAddHarmonicSelection}
                                isSideBandsSelectionMode={isSideBandsSelectionMode}
                                onGenerateSideBandsFromClick={onGenerateSideBandsFromClick}
                                onHarmonicLineClick={onHarmonicLineClick}
                                onVerticalLineClick={onVerticalLineClick}
                                focusedHarmonicColor={focusedHarmonicColor}
                                benchmarkLineId={selectedBenchmarkId}
                                selectedHarmonicLineId={selectedHarmonicLineId}
                                focusedSensor={focusedSensor}
                                pendingHarmonicCreation={pendingHarmonicCreation}
                                onCancelPendingHarmonic={onCancelPendingHarmonic}
                                showDualLabels={showDualLabels}
                                showComparisonTable={showComparisonTable}
                                selectedMarkerLabel={selectedMarkerLabel}
                                selectedTimestampsSet={selectedTimestampsSet}
                                markerLabel={markerLabel}
                                showFloatingToolbar={showFloatingToolbar}
                                onEnterMarkerSelectionMode={onEnterMarkerSelectionMode}
                                onEnterHarmonicSelectionMode={onEnterHarmonicSelectionMode}
                                onEnterSideBandsSelectionMode={onEnterSideBandsSelectionMode}
                                harmonicSelections={harmonicSelections}
                                sideBandsSelections={sideBandsSelections}
                                onUpdateHarmonicSelection={onUpdateHarmonicSelection}
                                onRemoveHarmonicSelection={onRemoveHarmonicSelection}
                                onRemoveAllHarmonics={onRemoveAllHarmonics}
                                onResetHarmonic={onResetHarmonic}
                                onGenerateHarmonics={onGenerateHarmonics}
                                onToggleCompareModal={onToggleCompareModal}
                                onClearLines={onClearLines}
                                expandableLegendSeries={expandableLegendSeries}
                                expandableLegendFocusMode={focusMode}
                                isCustomLineMode={isCustomLineMode}
                                onEnterCustomLineMode={onEnterCustomLineMode}
                                setCustomLineMenuState={setCustomLineMenuState}
                                focusedCustomGroupId={focusedCustomGroupId}
                                autoscaleMode={autoscaleMode}
                                autoscaleAxis={autoscaleAxis}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                // Normal split mode - split by SENSORS
                const enabledSensorData = getValidEnabledSensorData(data, enabledSensors, isWaveformDataLayer);

                if (enabledSensorData.length === 0) {
                  return (
                    <div key={metric.key} className="col-span-10">
                      <ChartEmptyState
                        type={EMPTY_STATE_ENUM.NO_SENSORS}
                        className={splitColumns > 2 ? 'max-h-[180px]' : ''}
                      />
                    </div>
                  );
                }

                return (
                  <div key={metric.key}>
                    <div
                      className={`grid gap-4 ${
                        splitColumns === 1
                          ? 'grid-cols-1'
                          : splitColumns === 2
                          ? 'grid-cols-1 lg:grid-cols-2'
                          : 'grid-cols-1 lg:grid-cols-3'
                      }`}
                    >
                      {enabledSensorData.map((sensorData: any, index: number) => {
                        const sensorName = sensorData.displayName || sensorData.name;
                        const sensorColor = getSensorColor(sensorName, metric.sensorColors, index);

                        return (
                          <div
                            key={`${metric.key}-${sensorName}`}
                            className="relative bg-[#f0f2f7] rounded"
                            data-testid={`graph_split_${generateTestId(metric.key)}_${generateTestId(sensorName)}`}
                          >
                            <PlotlyChart
                              data={data}
                              title={sensorName}
                              unit={metric.unit}
                              isDiscrete={isDiscrete}
                              height={isDiscrete ? 200 : 300}
                              colors={[sensorColor]}
                              labels={[sensorName]}
                              enabledSensors={{ [sensorName]: true }}
                              verticalLines={verticalLines}
                              customVerticalLines={data?.customVerticalLines || customVerticalLines}
                              thresholds={thresholdsBySensor[sensorData.displayName]}
                              chartType={metric.key}
                              sensorName={sensorName}
                              sectionId={sectionId}
                              dataLayer={dataLayer}
                              chartId={`${metric.key}-${sensorName.toLowerCase().replace(/\s+/g, '-')}`}
                              sharedTimeRange={sharedTimeRange}
                              onRangeChange={handleRangeChange}
                              onEditThresholds={() => handleEditThresholds(metric.key, sensorName)}
                              showThresholds={true}
                              thresholdVariant={thresholdVariant}
                              intervalLines={intervalLines}
                              onRemoveIntervalLine={removeIntervalLine}
                              selectionMode={selectionMode}
                              selectedRanges={selectedRanges}
                              onAddSelectedRange={handleAddSelectedRange}
                              onRemoveSelectedRange={handleRemoveSelectedRange}
                              onAddVerticalLine={addVerticalLine}
                              onRemoveVerticalLine={removeVerticalLine}
                              onUpdateVerticalLine={updateVerticalLine}
                              globalYRange={null}
                              showUnits={isSplit}
                              isComparisonEnabled={true}
                              splitColumns={splitColumns}
                              isMarkerSelectionMode={isMarkerSelectionMode}
                              isHarmonicSelectionMode={isHarmonicSelectionMode}
                              onAddHarmonicSelection={onAddHarmonicSelection}
                              isSideBandsSelectionMode={isSideBandsSelectionMode}
                              onGenerateSideBandsFromClick={onGenerateSideBandsFromClick}
                              onHarmonicLineClick={onHarmonicLineClick}
                              onVerticalLineClick={onVerticalLineClick}
                              focusedHarmonicColor={focusedHarmonicColor}
                              benchmarkLineId={selectedBenchmarkId}
                              selectedHarmonicLineId={selectedHarmonicLineId}
                              focusedSensor={focusedSensor}
                              pendingHarmonicCreation={pendingHarmonicCreation}
                              onCancelPendingHarmonic={onCancelPendingHarmonic}
                              showDualLabels={showDualLabels}
                              showComparisonTable={showComparisonTable}
                              selectedMarkerLabel={selectedMarkerLabel}
                              selectedTimestampsSet={selectedTimestampsSet}
                              markerLabel={markerLabel}
                              showFloatingToolbar={showFloatingToolbar}
                              onEnterMarkerSelectionMode={onEnterMarkerSelectionMode}
                              onEnterHarmonicSelectionMode={onEnterHarmonicSelectionMode}
                              onEnterSideBandsSelectionMode={onEnterSideBandsSelectionMode}
                              harmonicSelections={harmonicSelections}
                              sideBandsSelections={sideBandsSelections}
                              onUpdateHarmonicSelection={onUpdateHarmonicSelection}
                              onRemoveHarmonicSelection={onRemoveHarmonicSelection}
                              onRemoveAllHarmonics={onRemoveAllHarmonics}
                              onResetHarmonic={onResetHarmonic}
                              onGenerateHarmonics={onGenerateHarmonics}
                              onToggleCompareModal={onToggleCompareModal}
                              onClearLines={onClearLines}
                              expandableLegendSeries={expandableLegendSeries}
                              expandableLegendFocusMode={focusMode}
                              isCustomLineMode={isCustomLineMode}
                              onEnterCustomLineMode={onEnterCustomLineMode}
                              setCustomLineMenuState={setCustomLineMenuState}
                              focusedCustomGroupId={focusedCustomGroupId}
                              autoscaleMode={autoscaleMode}
                              autoscaleAxis={autoscaleAxis}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()
            ) : (
              <div
                key={metric.key}
                className="relative rounded h-full w-full"
                data-testid={`graph_combined_${generateTestId(metric.key)}`}
              >
                {isWaveformDataLayer ? (
                  <>
                    {waveformVariant === 'wavesurfer' ? (
                      <MultiWaveSurferChart
                        traces={waveformTraces}
                        allTraces={allWaveformTraces}
                        onTraceSelect={handleWaveformTraceSelect}
                        height={320}
                        enableZoom={true}
                        cursorColor="#ef4444"
                        onControlsReady={handleWaveformControlsReady}
                        onPlaybackStateChange={handleWaveformPlaybackStateChange}
                        markers={waveformMarkers}
                        isMarkerSelectionMode={isMarkerSelectionMode}
                        onAddMarker={handleWaveformAddMarker}
                        onMarkerClick={(markerId, position) => {
                          // Handle marker click - open context menu or select
                          onVerticalLineClick?.(markerId, position);
                        }}
                        selectedMarkerId={selectedMarkerLabel ? verticalLines.find((l: any) => l.label === selectedMarkerLabel)?.id : null}
                        isHarmonicSelectionMode={isHarmonicSelectionMode}
                        isSideBandsSelectionMode={isSideBandsSelectionMode}
                        onHarmonicClick={(time) => {
                          // For waveform, pass the time value to the harmonic handler
                          // The parent component (DataLayerDashboardContainer) handles the two-click workflow
                          if (isHarmonicSelectionMode) {
                            onAddHarmonicSelection?.({
                              time,
                              xAxisType: 'numeric',
                              chartId: `${sectionId}-waveform`,
                            });
                          } else if (isSideBandsSelectionMode) {
                            onGenerateSideBandsFromClick?.(time, 'numeric', `${sectionId}-waveform`);
                          }
                        }}
                        pendingHarmonicCreation={pendingHarmonicCreation ? {
                          centerPoint: pendingHarmonicCreation.centerPoint,
                          type: pendingHarmonicCreation.type,
                          labelPrefix: pendingHarmonicCreation.labelPrefix,
                          color: pendingHarmonicCreation.color,
                          groupId: pendingHarmonicCreation.groupId,
                        } : null}
                      />
                    ) : (
                      // PlotlyChart-based waveform variant - shows all enabled traces (acceleration/velocity)
                      <PlotlyChart
                        data={waveformPlotlyData}
                        title=""
                        unit="g / mm/s"
                        height={320}
                        isDiscrete={false}
                        colors={waveformTraces.map(t => t.color)}
                        labels={waveformTraces.map(t => t.name)}
                        enabledSensors={waveformTraces.reduce((acc, t) => ({ ...acc, [t.name]: true }), {})}
                        verticalLines={verticalLines}
                        customVerticalLines={customVerticalLines}
                        thresholds={[]}
                        showThresholds={false}
                        chartType="waveform"
                        sectionId={sectionId}
                        dataLayer={dataLayer}
                        chartId={`waveform-plotly-${sectionId}`}
                        sharedTimeRange={sharedTimeRange}
                        onRangeChange={handleRangeChange}
                        onEditThresholds={() => {}}
                        intervalLines={intervalLines}
                        onRemoveIntervalLine={removeIntervalLine}
                        selectionMode={selectionMode}
                        selectedRanges={selectedRanges}
                        onAddSelectedRange={handleAddSelectedRange}
                        onRemoveSelectedRange={handleRemoveSelectedRange}
                        onAddVerticalLine={addVerticalLine}
                        onRemoveVerticalLine={removeVerticalLine}
                        onUpdateVerticalLine={updateVerticalLine}
                        globalYRange={null}
                        isComparisonEnabled={false}
                        splitColumns={splitColumns}
                        isMarkerSelectionMode={isMarkerSelectionMode}
                        isHarmonicSelectionMode={isHarmonicSelectionMode}
                        onAddHarmonicSelection={onAddHarmonicSelection}
                        isSideBandsSelectionMode={isSideBandsSelectionMode}
                        onGenerateSideBandsFromClick={onGenerateSideBandsFromClick}
                        onHarmonicLineClick={onHarmonicLineClick}
                        onVerticalLineClick={onVerticalLineClick}
                        focusedHarmonicColor={focusedHarmonicColor}
                        pendingHarmonicCreation={pendingHarmonicCreation}
                        onCancelPendingHarmonic={onCancelPendingHarmonic}
                        showFloatingToolbar={showFloatingToolbar}
                        onEnterMarkerSelectionMode={onEnterMarkerSelectionMode}
                        onEnterHarmonicSelectionMode={onEnterHarmonicSelectionMode}
                        onEnterSideBandsSelectionMode={onEnterSideBandsSelectionMode}
                        harmonicSelections={harmonicSelections}
                        sideBandsSelections={sideBandsSelections}
                        onUpdateHarmonicSelection={onUpdateHarmonicSelection}
                        onRemoveHarmonicSelection={onRemoveHarmonicSelection}
                        onRemoveAllHarmonics={onRemoveAllHarmonics}
                        onResetHarmonic={onResetHarmonic}
                        onGenerateHarmonics={onGenerateHarmonics}
                        onClearLines={onClearLines}
                        autoscaleMode={autoscaleMode}
                        autoscaleAxis={autoscaleAxis}
                        waveformPlaybackTime={plotlyPlayheadTime}
                        waveformDuration={plotlyWaveformDuration}
                        onWaveformSeek={(time) => {
                          const clampedTime = Math.max(0, Math.min(time, plotlyWaveformDuration));
                          setPlotlyPlayheadTime(clampedTime);
                        }}
                      />
                    )}
                  </>
                ) : (
                <PlotlyChart
                  data={data}
                  title={''}
                  unit={metric.unit}
                  height={300}
                  isDiscrete={isDiscrete}
                  colors={Object.keys(validEnabledSensors).map((sensorName, index) =>
                    getSensorColor(sensorName, metric.sensorColors, index)
                  )}
                  labels={Object.keys(validEnabledSensors)}
                  enabledSensors={(() => {
                    if (dataLayer?.includes('alarm')) {
                      // Check if enabledSensors has nested structure (by sectionId) or flat structure (sensor names as keys)
                      let alarmEnabledSensors: Record<string, boolean>;

                      const sectionData = enabledSensors[sectionId];
                      if (sectionData && typeof sectionData === 'object' && !Array.isArray(sectionData)) {
                        // Nested structure: use the section-specific sensors
                        alarmEnabledSensors = sectionData as Record<string, boolean>;
                      } else {
                        // Flat structure: check if any alarm sensor names exist as top-level keys
                        const sensorNames = Object.keys(validEnabledSensors);
                        const hasFlat = sensorNames.some(name => name in enabledSensors);

                        if (hasFlat) {
                          // Use flat structure - extract only alarm sensors from top-level
                          alarmEnabledSensors = {};
                          sensorNames.forEach(name => {
                            const value = enabledSensors[name];
                            if (name in enabledSensors && typeof value === 'boolean') {
                              alarmEnabledSensors[name] = value;
                            }
                          });
                        } else {
                          // No enabled sensors found, fallback to all sensors
                          alarmEnabledSensors = validEnabledSensors;
                        }
                      }

                      return alarmEnabledSensors;
                    }
                    return validEnabledSensors;
                  })()}
                  verticalLines={verticalLines}
                  customVerticalLines={data?.customVerticalLines || customVerticalLines}
                  thresholds={allThresholds}
                  showThresholds={false}
                  onAddThreshold={addThreshold}
                  onRemoveThreshold={removeThreshold}
                  chartType={metric.key}
                  sectionId={sectionId}
                  dataLayer={dataLayer}
                  chartId={`${metric.key}-combined`}
                  sharedTimeRange={sharedTimeRange}
                  onRangeChange={handleRangeChange}
                  intervalLines={intervalLines}
                  onRemoveIntervalLine={removeIntervalLine}
                  selectionMode={selectionMode}
                  selectedRanges={selectedRanges}
                  onAddSelectedRange={handleAddSelectedRange}
                  onRemoveSelectedRange={handleRemoveSelectedRange}
                  onAddVerticalLine={addVerticalLine}
                  onRemoveVerticalLine={removeVerticalLine}
                  onUpdateVerticalLine={updateVerticalLine}
                  globalYRange={getGlobalYRange(metric.key)}
                  isComparisonEnabled={true}
                  splitColumns={splitColumns}
                  onEditThresholds={() => handleEditThresholds(metric.key)}
                  isMarkerSelectionMode={isMarkerSelectionMode}
                  isHarmonicSelectionMode={isHarmonicSelectionMode}
                  onAddHarmonicSelection={onAddHarmonicSelection}
                  isSideBandsSelectionMode={isSideBandsSelectionMode}
                  onGenerateSideBandsFromClick={onGenerateSideBandsFromClick}
                  onHarmonicLineClick={onHarmonicLineClick}
                  onVerticalLineClick={onVerticalLineClick}
                  focusedHarmonicColor={focusedHarmonicColor}
                  benchmarkLineId={selectedBenchmarkId}
                  selectedHarmonicLineId={selectedHarmonicLineId}
                  focusedSensor={focusedSensor}
                  pendingHarmonicCreation={pendingHarmonicCreation}
                  onCancelPendingHarmonic={onCancelPendingHarmonic}
                  showDualLabels={showDualLabels}
                  showComparisonTable={showComparisonTable}
                  selectedMarkerLabel={selectedMarkerLabel}
                  selectedTimestampsSet={selectedTimestampsSet}
                  markerLabel={markerLabel}
                  showFloatingToolbar={showFloatingToolbar}
                  onEnterMarkerSelectionMode={onEnterMarkerSelectionMode}
                  onEnterHarmonicSelectionMode={onEnterHarmonicSelectionMode}
                  onEnterSideBandsSelectionMode={onEnterSideBandsSelectionMode}
                  harmonicSelections={harmonicSelections}
                  sideBandsSelections={sideBandsSelections}
                  onUpdateHarmonicSelection={onUpdateHarmonicSelection}
                  onRemoveHarmonicSelection={onRemoveHarmonicSelection}
                  onRemoveAllHarmonics={onRemoveAllHarmonics}
                  onResetHarmonic={onResetHarmonic}
                  onGenerateHarmonics={onGenerateHarmonics}
                  onToggleCompareModal={onToggleCompareModal}
                  onClearLines={onClearLines}
                  expandableLegendSeries={expandableLegendSeries}
                  expandableLegendFocusMode={focusMode}
                  isCustomLineMode={isCustomLineMode}
                  onEnterCustomLineMode={onEnterCustomLineMode}
                  setCustomLineMenuState={setCustomLineMenuState}
                  focusedCustomGroupId={focusedCustomGroupId}
                  autoscaleMode={autoscaleMode}
                  autoscaleAxis={autoscaleAxis}
                />
                )}
              </div>
            )
          ) : null
        )}
      </div>

      {/* Comparison Modal */}
      {renderComparisonModal()}
    </div>
  );
};
